import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSupportMedia } from './supportApi';
import './SupportVoicePlayer.css';

const WAVE = [13, 20, 16, 31, 23, 12, 28, 36, 19, 32, 14, 24, 39, 20, 29, 15, 34, 23, 17, 35, 27, 14, 26, 39, 18, 30, 22, 12, 33, 24, 17, 28, 35, 16];
const timecode = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const value = Math.floor(seconds);
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

function Icon({ name }) {
  const base = { width: 19, height: 19, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'play') return <svg {...base}><path d="m8 5 11 7-11 7z" fill="currentColor" stroke="none"/></svg>;
  if (name === 'pause') return <svg {...base}><path d="M9 5v14M15 5v14" strokeWidth="4"/></svg>;
  if (name === 'mic') return <svg {...base} width={15} height={15}><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8"/></svg>;
  if (name === 'retry') return <svg {...base} width={13} height={13}><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"/></svg>;
  return <svg {...base} className="gp-vp22-spinner"><path d="M12 3a9 9 0 1 0 9 9"/></svg>;
}

/** Uses the existing private media API; no save/download button. */
export default function SupportVoicePlayer({ message, token, apiUrl }) {
  const playerRef = useRef(null);
  const requestRef = useRef(null);
  const objectUrlRef = useRef('');
  const busyRef = useRef(false);
  const playAfterLoadRef = useRef(false);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [bytes, setBytes] = useState(0);
  const [percent, setPercent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(Math.max(0, (message.media?.durationMs || 0) / 1000));
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');

  useEffect(() => {
    setUrl(''); setLoading(false); setBytes(0); setPercent(null);
    setPlaying(false); setElapsed(0); setError(''); setHint('');
    setDuration(Math.max(0, (message.media?.durationMs || 0) / 1000));
    return () => {
      requestRef.current?.abort();
      playerRef.current?.pause();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
      requestRef.current = null;
      busyRef.current = false;
      playAfterLoadRef.current = false;
    };
  }, [message._id, message.media?.durationMs, token, apiUrl]);

  const loadAudio = useCallback(async () => {
    if (busyRef.current || !token || message.media?.expired) return;
    const controller = new AbortController();
    requestRef.current = controller;
    busyRef.current = true;
    playAfterLoadRef.current = true;
    setLoading(true); setError(''); setHint(''); setBytes(0); setPercent(null);
    try {
      const response = await fetchSupportMedia({
        apiUrl, token, messageId: message._id, signal: controller.signal, voiceMp3: true,
        onProgress: (loaded, total) => {
          if (controller.signal.aborted) return;
          setBytes(loaded);
          setPercent(total && total > 0 ? Math.min(100, Math.round(loaded / total * 100)) : null);
        },
      });
      if (controller.signal.aborted) return;
      const mp3 = new Blob([response], { type: 'audio/mpeg' });
      if (!mp3.size) throw new Error('Empty voice recording.');
      const newUrl = URL.createObjectURL(mp3);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = newUrl;
      setElapsed(0); setUrl(newUrl);
    } catch (err) {
      if (!controller.signal.aborted) {
        playAfterLoadRef.current = false;
        setError(err.message || 'Could not load voice message.');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
      busyRef.current = false;
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [apiUrl, token, message._id, message.media?.expired]);

  useEffect(() => {
    if (!url || !playAfterLoadRef.current) return;
    playAfterLoadRef.current = false;
    void playerRef.current?.play().catch(() => {
      setPlaying(false);
      setHint('Ready to play · tap ▶ again');
    });
  }, [url]);

  const toggle = async () => {
    if (loading) return;
    if (!url) { await loadAudio(); return; }
    const player = playerRef.current;
    if (!player) return;
    if (!player.paused) { player.pause(); return; }
    try { setError(''); setHint(''); await player.play(); }
    catch { setPlaying(false); setError('Audio playback is unavailable. Tap Retry.'); }
  };
  const total = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const completion = total ? Math.max(0, Math.min(1, elapsed / total)) : 0;
  const canPlay = Boolean(token) && !message.media?.expired;

  return <div className="gp-vp22" onClick={event => event.stopPropagation()}>
    <audio ref={playerRef} src={url || undefined} preload="metadata" playsInline
      onLoadedMetadata={event => { const value = event.currentTarget.duration; if (Number.isFinite(value) && value > 0) setDuration(value); }}
      onDurationChange={event => { const value = event.currentTarget.duration; if (Number.isFinite(value) && value > 0) setDuration(value); }}
      onTimeUpdate={event => setElapsed(event.currentTarget.currentTime || 0)}
      onPlay={() => { setPlaying(true); setHint(''); }} onPause={() => setPlaying(false)}
      onEnded={() => { setPlaying(false); setElapsed(0); if (playerRef.current) playerRef.current.currentTime = 0; }}
      onError={() => { if (url) { setPlaying(false); setError('This voice note could not be decoded. Try again.'); } }} />
    <div className="gp-vp22-player">
      <button type="button" className="gp-vp22-play" disabled={!canPlay || loading}
        onClick={() => void toggle()}
        aria-label={loading ? 'Loading voice message' : playing ? 'Pause voice message' : 'Play voice message'}>
        <Icon name={loading ? 'loading' : playing ? 'pause' : 'play'}/>
      </button>
      <div className="gp-vp22-center">
        <div className="gp-vp22-wave">
          {WAVE.map((height, index) => <i key={index} className={index / WAVE.length <= completion ? 'gp-vp22-played' : ''}
            style={{ height: `${height}px` }} />)}
          <input type="range" min="0" max={total || 1} step="0.1" aria-label="Seek voice message"
            disabled={!url || !total} value={Math.min(elapsed, total || 1)}
            onChange={event => { const next = Number(event.target.value); if (playerRef.current) playerRef.current.currentTime = next; setElapsed(next); }} />
        </div>
        <div className="gp-vp22-info"><span>{timecode(elapsed)} <b>/</b> {timecode(total)}</span>
          <small>{playing ? 'Playing' : 'Voice message'}</small></div>
      </div>
      <div className={`gp-vp22-mic ${playing ? 'gp-vp22-mic-active' : ''}`} aria-hidden="true"><Icon name="mic" /></div>
    </div>
    {loading && <div className="gp-vp22-loading" aria-live="polite">
      <div><span>Loading audio…</span><span>{percent === null ? `${Math.round(bytes / 1024)} KB` : `${percent}%`}</span></div>
      <div className={`gp-vp22-rail ${percent === null ? 'gp-vp22-indeterminate' : ''}`}>
        <i style={percent !== null ? { width: `${percent}%` } : undefined}/>
      </div>
    </div>}
    {hint && !error && <p className="gp-vp22-hint" role="status">{hint}</p>}
    {error && <div className="gp-vp22-error" role="alert"><span>{error}</span>
      <button type="button" onClick={() => { setError(''); setHint(''); void loadAudio(); }} disabled={!canPlay || loading}>
        <Icon name="retry"/> Retry
      </button>
    </div>}
  </div>;
}
